<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { object, array, string } from 'yup'
import {
  DCard,
  DBtn,
  DTextField,
  DMultiselect,
  DSelectFieldSearch,
  DLoading,
  DModal
} from 'front-library'
import {
  getPayroll,
  getWorkspace,
  ICreateAPIKey,
  createApiKeyManagement
} from '@/app/core/services/api-key-management'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const store = useUserStore()
const router = useRouter()
const notifications = useNotificationsStore()
const apiKey = ref(null)
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const typeAlertModal = ref('')
const titleModal = ref('')
const descriptionModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const searchRoles = ref('')
const itemsRoles = ref<any[]>([])

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  allowedPayrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  allowedRol: string().required(t('form.valid.required')),
  name: string()
    .max(100, t('form.valid.maxLength', [100]))
    .required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: allowedPayrolls } = useField<string[]>(ref('allowedPayrolls'))
const { value: allowedRol } = useField<string>('allowedRol')
const { value: name } = useField<string>('name')
const { value: expiresAt } = useField<string>(ref('expiresAt'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: ICreateAPIKey) => {
  if (!expiresAt.value) {
    notifications.addNotification({
      title: t('apiKeys.notification.warning'),
      text: t('apiKeys.notification.warningMessage'),
      type: 'warning',
      time: 10000
    })
  }
  void createApiKey(values)
})

/* Crear solicitud del reporte */
const createApiKey = async (values: any) => {
  loading.value = true
  messageLoading.value = t('apiKeys.loading.generatingApiKey')
  try {
    const response: any = await createApiKeyManagement(values)
    apiKey.value = response.apikey
    showModal.value = true
    typeAlertModal.value = 'success'
    titleModal.value = t('notification.created')
    descriptionModal.value = t('apiKeys.notification.created')
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'apiKeys', t, notifications, loading)
  }
}

/* Copiar apiKey creada */
const copyApiKey = async (text: string) => {
  await navigator.clipboard.writeText(text)
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'ListApiKeys' })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
  void router.push({ name: 'ListApiKeys' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const [promisePayrolls, promiseRoles] = await Promise.all([
      getPayroll(),
      getWorkspace(store.user?.workspaceId)
    ])
    itemsPayrolls.value = promisePayrolls
    itemsRoles.value = promiseRoles
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'apiKeys', t, notifications, loading)
  }
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="apiKey != null">
        <div class="text-center tracking-widest text-black text-sm">
          <span>{{ apiKey }}</span>
        </div>
        <div
          class="underline tracking-widest text-blue-600 cursor-pointer"
          @click="copyApiKey(apiKey)"
          v-html="t('apiKeys.notification.copyApiKey')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('apiKeys.create')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-4 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('apiKeys.fields.payrolls')"
                    v-model="allowedPayrolls"
                    :headerTitle="t('apiKeys.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.allowedPayrolls" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('apiKeys.fields.role')"
                    v-model="allowedRol"
                    :required="true"
                    :options="filterOptions(itemsRoles, searchRoles)"
                    @filter="searchRoles = $event"
                    :error="errors.allowedRol" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('apiKeys.fields.name')"
                    v-model="name"
                    :required="true"
                    :error="errors.name" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('apiKeys.fields.dueDate')"
                    v-model="expiresAt" />
                </div>
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.save')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
